import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { LDProvider } from 'parcels/src/common/LDProvider';
import { setGeoLocationToStorage } from 'parcels/src/common/PSXHeader/SearchBar/utils';
import { DocumentDirectory } from 'parcels/src/frontends/DocumentDirectory/DocumentDirectory';
import { GuestDeeplinkGenerator } from 'parcels/src/frontends/GuestExperience/deeplink/GuestDeeplinkGenerator';
import { EarlyPayContainer } from 'parcels/src/frontends/GuestExperience/EarlyPayContainer';
import { GuestExperience } from 'parcels/src/frontends/GuestExperience/GuestExperience';
import { getLanguageFromCookie } from 'parcels/src/frontends/ProviderSearch/context/Internationalization/helpers';
import { ProviderSearchForPortal } from 'parcels/src/frontends/ProviderSearchForPortal/ProviderSearchForPortal';
import { useGuestStore } from 'parcels/src/store/useGuestStore';
import { GuestState } from 'parcels/src/store/useGuestStore/guestStore';
import React, { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  Constants,
  LOB as LOBList,
  PSX_LANDING_PAGE_PARAMS,
  PopulationType,
  RECIPROCITYID_NEVADA,
} from '../../parcels/src/common/Constants';
import {
  PortalData,
  PortalIdentifier,
  PortalName,
} from '../../parcels/src/common/Portal';
import { MockAuthPortalMemberInfo } from '../../parcels/src/common/Portal/MockAuthMember';
import {
  mockBehavioralGuestPortalMemberInfo,
  mockTier1PortalMemberInfo,
} from '../../parcels/src/common/Portal/mockBehavioralGuestPortalMemberInfo';
import {
  MockCnSPlans,
  MockMnRPlans,
  defaultPopulationForLob,
  getGuestPortalMemberInfo,
  getReciprocityIds,
  mockMedicalGuestPortalMemberInfo,
} from '../../parcels/src/common/Portal/mockMedicalGuestPortalMemberInfo';
import { mockPortalMemberInfo } from '../../parcels/src/common/Portal/mockPortalMemberInfo';
import { ProviderSearch } from '../../parcels/src/frontends/ProviderSearch';
import { DetailsDrawer } from './DetailsDrawer';
import SmartChoiceDemo from './SmartChoiceDemo';
import { createThemeOverride, portalNameThemeMap } from './theme/theme';

export const browser = () => {
  const { getLocation } = useRouter();
  const { pathname = '' } = getLocation() || {};
  const lastSegment = pathname.split('/').at(-1) || '';

  const { clearGuestData, setGuestData } = useGuestStore(
    useShallow((state: GuestState) => ({
      setGuestData: state.setGuestData,
      clearGuestData: state.clearGuestData,
    }))
  );

  useEffect(() => {
    const isGuest = pathname.startsWith('/guest');
    if (!isGuest) {
      clearGuestData();
    }
  }, [clearGuestData]);

  // Enhanced Base64 validation
  const isBase64 = (str: string) => {
    // Check if the string matches Base64 format and has a length that is a multiple of 4
    const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
    if (!base64Regex.test(str) || str.length % 4 !== 0) {
      return false;
    }

    // Attempt to decode the string and check if it produces valid JSON
    try {
      const decoded = Buffer.from(str, 'base64').toString('utf-8');
      JSON.parse(decoded); // Ensure it's valid JSON
      return true;
    } catch {
      return false;
    }
  };

  if (isBase64(lastSegment)) {
    // If not entering on the /guest root path redirect to avoid colliding with the logic that clears guest data when
    // not in the guest flow
    if (!pathname.startsWith('/guest')) {
      window.location.replace(`/guest/${lastSegment}`);
      return null;
    }

    const payload = JSON.parse(
      Buffer.from(decodeURIComponent(lastSegment), 'base64').toString('utf-8')
    );

    setGuestData(payload);
    setGeoLocationToStorage({});
    window.location.replace('/guest/');
  }

  if (pathname === '/details-widget') {
    return <DetailsDrawer />;
  }

  if (pathname === '/smart-choice-widget') {
    return <SmartChoiceDemo />;
  }

  if (pathname.startsWith('/laww')) {
    // Parse the current URL
    const urlParams = new URLSearchParams(window.location.search);

    // get theme
    const theme = urlParams.get('theme') || 'optum';

    // Get language, defaults to en-US
    const lang = urlParams.get('lang') || getLanguageFromCookie() || 'en-US';

    // Get the user tier, defaults to 2
    const userTier = urlParams.get('userTier') || '2';
    const eapOnlyClient = urlParams.get('eapOnlyClient') === 'true';
    const showEapFilter =
      eapOnlyClient || urlParams.get('showEapFilter') !== 'false';
    const lockEapFilter =
      eapOnlyClient || urlParams.get('lockEapFilter') === 'true';
    const preSelectEapFilter =
      eapOnlyClient || urlParams.get('preSelectEapFilter') === 'true';
    const showOnlineScheduling =
      urlParams.get('showOnlineScheduling') !== 'false';
    const portalShowCost = !(
      eapOnlyClient || urlParams.get('showCost') === 'false'
    );
    const showTreatmentCenter = !(
      eapOnlyClient || urlParams.get('showTreatmentCenter') === 'false'
    );
    const showMarketPlace = !(
      eapOnlyClient || urlParams.get('showMarketPlace') === 'false'
    );
    const isBccEligible = urlParams.get('isBccEligible') === 'true';

    const memberInfo = () => {
      if (userTier === '0') return mockBehavioralGuestPortalMemberInfo;
      if (userTier === '1') return mockTier1PortalMemberInfo;
      return mockPortalMemberInfo;
    };
    const showVirtualVisits = urlParams.get('showVirtualVisits') !== 'false';

    const portalData: PortalData = {
      portalName: PortalName.LAWW,
      portalConfig: {
        basePath: '/laww',
        headerChipsEnabled: false,
        theme,
        language: lang,
        homePage: PSX_LANDING_PAGE_PARAMS,
        signInUrl: '/api/auth/server-signin?callbackUrl=LANDING_PAGE',
        isGuestUser: userTier === '0',
        showCost: portalShowCost,
        showTreatmentCenter,
        showMarketPlace,
        includeBaseCss: true,
        userTier,
        showEapFilter,
        lockEapFilter,
        preSelectEapFilter,
        showVirtualVisits,
        showOnlineScheduling,
        stickyHeaderDisabled: true,
        isBccEligible,
      },
      portalMemberInfo: memberInfo(),
      portalUrls: [],
    };

    return <ProviderSearchForPortal portal-data={JSON.stringify(portalData)} />;
  }

  if (
    pathname.startsWith('/umr') ||
    pathname.startsWith('/geha') ||
    pathname.startsWith('/umrNevada')
  ) {
    let themeOverride = createThemeOverride(portalNameThemeMap[PortalName.UMR]);
    let basePath = '/umr';
    let portalIdentifier = PortalIdentifier.STANDARD;
    let population = PopulationType.UM;
    if (pathname.startsWith('/geha')) {
      // Read the FF value from here if FF is enabled then go to V2
      themeOverride = createThemeOverride(
        portalNameThemeMap[PortalIdentifier.GEHA_V2]
      );
      basePath = '/geha';
      portalIdentifier = PortalIdentifier.GEHA_V2;

      const gehaReciprocityId = '10215';
      MockAuthPortalMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].reciprocityId =
        gehaReciprocityId;
      MockAuthPortalMemberInfo.memberInfo[0].reciprocityId = gehaReciprocityId;
      MockAuthPortalMemberInfo.memberInfo[0].policyNumber = '76416933';
    } else if (pathname.startsWith('/umrNevada')) {
      portalIdentifier = PortalIdentifier.NV;
      population = PopulationType.SIERRA;
      basePath = '/umrNevada';
      MockAuthPortalMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].reciprocityId =
        RECIPROCITYID_NEVADA;
      MockAuthPortalMemberInfo.memberInfo[0].reciprocityId =
        RECIPROCITYID_NEVADA;
      MockAuthPortalMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType =
        MockAuthPortalMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType.filter(
          (coverage) => coverage.typeCode.coverageTypeCode !== 'B'
        );
    }

    const {
      USE_PRE_PROD,
      USE_OBAPI,
      ENABLE_PCP,
      IS_PREPROD_USER,
      DEMO_MEMBER_INFO,
    } = Constants.STORAGE_KEYS.SESSION;

    const useObapiStore = storage.session.get(USE_OBAPI) || false;
    const useObapiPreProdStore = storage.session.get(USE_PRE_PROD) || false;
    const enablePcpStore = storage.session.get(ENABLE_PCP) || false;

    // Parse the current URL
    const urlParams = new URLSearchParams(window.location.search);

    // Get language, defaults to en-US
    const lang = urlParams.get('lang') || getLanguageFromCookie() || 'en-US';

    // Get enablePcp, defaults to false
    const urlPcpEnabled = urlParams.get('enablePcp');
    const isPcpEnabled = urlPcpEnabled
      ? urlParams.get('enablePcp') === 'true'
      : enablePcpStore;

    // Get greenDiamondPolicy state code
    const greenDiamondPolicy =
      urlParams.get('greenDiamondPolicy')?.toLowerCase() || '';

    const greenDiamondTestingPolicies = {
      wv: { policyId: '77700000', reciprocityId: '10169' },
    };

    const callingUser = urlParams.get('callingUser');
    const zipCode = urlParams.get('zipCode') || '';

    const { firstName, lastName, dob, policyId, memberId, delsysCode } =
      storage.session.get(DEMO_MEMBER_INFO) || {};

    const greenDiamondPolicyInfo =
      greenDiamondTestingPolicies[greenDiamondPolicy];

    // Get obapi related query params
    const memberFirstName = urlParams.get('firstName') || firstName;
    const memberLastName = urlParams.get('lastName') || lastName;
    const memberDob = urlParams.get('dob') || dob;
    const memberPolicyId = urlParams.get('policyId') || policyId;
    const urlMemberId = urlParams.get('memberId') || memberId;
    const memberDelsysCode = urlParams.get('delsysCode') || delsysCode;
    const urlEnablePreProd = urlParams.get('enablePreProd');
    const urlUseOBAPI = urlParams.get('useOBAPI');
    const enablePreProd = urlEnablePreProd
      ? urlParams.get('enablePreProd') === 'true'
      : useObapiPreProdStore;
    const useOBAPI = urlUseOBAPI
      ? urlParams.get('useOBAPI') === 'true'
      : useObapiStore;

    storage.session.set(DEMO_MEMBER_INFO, {
      firstName: memberFirstName,
      lastName: memberLastName,
      policyId: memberPolicyId,
      memberId: urlMemberId,
      dob: memberDob,
      delsysCode: memberDelsysCode,
      usePreProd: enablePreProd,
    });

    const portalConfig = {
      basePath,
      headerChipsEnabled: true,
      homePage: '/',
      language: lang,
      theme: 'uhc',
      includeBaseCss: true,
      isGuestUser: false,
      themeOverride,
      useOBAPI,
    };

    storage.session.set(IS_PREPROD_USER, enablePreProd);
    storage.session.set(USE_PRE_PROD, enablePreProd);
    storage.session.set(USE_OBAPI, useOBAPI);
    storage.session.set(ENABLE_PCP, isPcpEnabled);
    let portalData: PortalData;
    let portalEligibilityData;
    if (!useOBAPI) {
      const portalMemberInfo = MockAuthPortalMemberInfo;
      portalMemberInfo.memberInfo[0].population = population;
      if (memberFirstName)
        portalMemberInfo.memberInfo[0].demographics.name.firstName =
          memberFirstName;
      if (memberLastName)
        portalMemberInfo.memberInfo[0].demographics.name.lastName =
          memberLastName;
      if (memberDob)
        portalMemberInfo.memberInfo[0].demographics.dateOfBirth = memberDob;
      if (memberDelsysCode) {
        portalMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].reciprocityId =
          memberDelsysCode;
        portalMemberInfo.memberInfo[0].reciprocityId = memberDelsysCode;
      }
      if (memberPolicyId)
        portalMemberInfo.memberInfo[0].policyNumber = memberPolicyId;
      portalMemberInfo.memberInfo[0].viewPcp = isPcpEnabled;
      portalMemberInfo.memberInfo[0].editPcp = isPcpEnabled;
      if (greenDiamondPolicyInfo) {
        portalMemberInfo.memberInfo[0].reciprocityId =
          greenDiamondPolicyInfo.reciprocityId;
        portalMemberInfo.memberInfo[0].policyNumber =
          greenDiamondPolicyInfo.policyId;
        portalMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].reciprocityId =
          greenDiamondPolicyInfo.reciprocityId;
      }

      portalData = {
        portalName: PortalName.UMR,
        portalIdentifier,
        portalConfig,
        portalMemberInfo,
      };
    } else {
      portalEligibilityData = {
        firstName: memberFirstName,
        lastName: memberLastName,
        dob: memberDob,
        memberId: urlMemberId,
        policyId: memberPolicyId,
        useObapiPreprod: enablePreProd,
        delsysCode: memberDelsysCode,
        enablePcp: isPcpEnabled,
        callingUser,
        zipCode,
      };
      portalData = {
        portalName: PortalName.UMR,
        portalIdentifier,
        portalEligibilityData,
        portalConfig,
      };
    }

    return <ProviderSearchForPortal portal-data={JSON.stringify(portalData)} />;
  }

  if (pathname.startsWith('/guest-with-plan')) {
    // Parse the current URL
    const urlParams = new URLSearchParams(window.location.search);
    const { IS_GUEST, GUEST_USER } = Constants.STORAGE_KEYS.SESSION;

    const isGuestUser = storage.session.get(IS_GUEST) || false;
    const guestUserData = storage.session.get(GUEST_USER) || null;
    let lob = urlParams.get('lob')?.toLocaleLowerCase() || 'cns';
    let coverageType = urlParams.get('coverageType') || 'M';
    let isDsnp = urlParams.get('isDsnp') === 'true';
    let population = urlParams.get('population') || '';
    let membershipCategory = urlParams.get('membershipCategory') || '';
    let reciprocityId = urlParams.get('reciprocityId') || '';
    let lang = urlParams.get('lang') || getLanguageFromCookie() || 'en-US';
    let situsState = urlParams.get('situsState') || '';
    const isGeha = urlParams.get('portal') === 'GEHA';

    if (
      !urlParams.get('lob') &&
      !urlParams.get('population') &&
      isGuestUser &&
      guestUserData &&
      guestUserData.lineOfBusiness &&
      guestUserData.population
    ) {
      const lineOfBusinessKey = Object.keys(LOBList).find(
        (key) =>
          LOBList[key]?.toLocaleLowerCase() ===
          guestUserData.lineOfBusiness?.toLocaleLowerCase()
      );

      lob =
        lineOfBusinessKey || guestUserData.lineOfBusiness.toLocaleLowerCase();
      coverageType = guestUserData.coverageTypeCode || coverageType;
      isDsnp = guestUserData.isDsnp === true;
      population = guestUserData.population || population;
      membershipCategory =
        guestUserData.membershipCategory || membershipCategory;
      reciprocityId = guestUserData.reciprocityId || reciprocityId;
      lang = guestUserData.lang || lang;
      situsState = guestUserData.situsState || situsState;
    }

    population =
      population ||
      (isDsnp && lob === 'mnr'
        ? PopulationType.DSNP
        : defaultPopulationForLob[lob.toUpperCase()]);

    reciprocityId =
      reciprocityId ||
      getReciprocityIds({
        lob: lob.toUpperCase(),
        coverageType,
        population,
        membershipCategory,
      })[0];
    let planData = mockMedicalGuestPortalMemberInfo;
    if (
      lob === 'cns' &&
      population === 'Medicaid' &&
      reciprocityId === 'AZCAID'
    ) {
      if (coverageType === 'B') {
        planData = MockCnSPlans.behavioral;
      } else if (coverageType === 'M') {
        planData = MockCnSPlans.medical;
      } else if (coverageType === 'D') {
        planData = MockCnSPlans.dental;
      }
    } else if (lob === 'mnr') {
      if (coverageType === 'M') {
        if (population === PopulationType.DSNP) {
          planData = MockMnRPlans.medicalDsnp;
        } else {
          planData = MockMnRPlans.medicalCosmos;
        }
      } else if (coverageType === 'B') {
        planData = MockMnRPlans.behavioral;
      } else if (coverageType === 'D') {
        planData = MockMnRPlans.dental;
      } else if (coverageType === 'V') {
        planData = MockMnRPlans.vision;
      }
    } else {
      planData = getGuestPortalMemberInfo({
        lob,
        coverageType,
        population,
        reciprocityId,
        membershipCategory,
        situsState,
      });
    }

    let homePath = '/';
    if (coverageType === 'B') {
      homePath = '/?chipValue=Behavioral%20Health&coverageType=B';
    } else if (coverageType === 'D') {
      homePath = '/?chipValue=Dental&coverageType=D';
    } else if (coverageType === 'V') {
      homePath = '/?chipValue=Vision&coverageType=V';
    }
    let portalIdentifier;
    let themeOverride;

    if (isGeha) {
      themeOverride = createThemeOverride(
        portalNameThemeMap[PortalIdentifier.GEHA_V2]
      );

      portalIdentifier = PortalIdentifier.GEHA_V2;
      planData = getGuestPortalMemberInfo({
        lob: 'eni',
        coverageType,
        population: defaultPopulationForLob[lob.toUpperCase()],
        reciprocityId: '52',
        membershipCategory,
        situsState: '',
      });
    }

    const portalData: PortalData = {
      portalName: PortalName.GUEST_PLAN_SELECTION,
      ...(isGeha && { portalIdentifier }),
      portalConfig: {
        basePath: '/guest-with-plan',
        headerChipsEnabled: coverageType === 'M',
        ...(isGeha && { themeOverride }),
        theme: 'uhc',
        language: lang, // Get language, defaults to en-US
        homePage: homePath,
        isGuestUser: true,
        showCost: true,
        showTreatmentCenter: true,
        includeBaseCss: true,
        showVirtualVisits: true,
        showOnlineScheduling: true,
      },
      portalMemberInfo: planData,
      portalUrls: [],
    };

    return <ProviderSearchForPortal portal-data={JSON.stringify(portalData)} />;
  }

  if (pathname.startsWith('/guest-plan-selection')) {
    const hostname = window?.location?.hostname;
    if (hostname === 'localhost') {
      storage.cookie.set('LOB-Target', 'guest-plan-selection');
    } else {
      document.cookie = `LOB-Target=guest-plan-selection; Domain=.${hostname}; path=/;`;
    }
    return <GuestExperience basePath="/guest-plan-selection" />;
  }

  if (pathname.startsWith('/guest')) {
    return (
      <LDProvider>
        <EarlyPayContainer />
      </LDProvider>
    );
  }

  if (pathname.startsWith('/generate-guest-deeplink')) {
    return <GuestDeeplinkGenerator />;
  }

  if (pathname.startsWith('/supportFinder')) {
    const urlParams = new URLSearchParams(window.location.search);
    const userTier = urlParams.get('userTier') || '1';

    const memberInfo = () => {
      if (userTier === '0') return mockBehavioralGuestPortalMemberInfo;
      if (userTier === '1') return mockTier1PortalMemberInfo;
      return mockPortalMemberInfo;
    };

    const portalData: PortalData = {
      portalName: 'SUPPORT_FINDER',
      portalConfig: {
        basePath: '/supportFinder',
        headerChipsEnabled: false,
        homePage: PSX_LANDING_PAGE_PARAMS,
        signInUrl: '/api/auth/server-signin?callbackUrl=LANDING_PAGE',
        includeBaseCss: true,
        userTier,
        theme: 'optum',
        showEapFilter: true,
        lockEapFilter: true,
        preSelectEapFilter: true,
        stickyHeaderDisabled: true,
      },
      portalMemberInfo: memberInfo(),
      portalUrls: [],
    };

    return <ProviderSearchForPortal portal-data={JSON.stringify(portalData)} />;
  }

  if (pathname.startsWith('/provider-directory')) {
    return <DocumentDirectory />;
  }

  if (pathname.startsWith('/find-care')) {
    const portalData: PortalData = {
      portalName: PortalName.ONE_PORTAL,
      portalConfig: {
        basePath: '/find-care',
        headerChipsEnabled: false,
        homePage: '/',
        includeBaseCss: true,
        theme: 'uhc',
        useOBAPI: true,
        usePlanSelector: true,
      },
    };

    return <ProviderSearchForPortal portal-data={JSON.stringify(portalData)} />;
  }

  return <ProviderSearch />;
};
